import { faCopy, faPlay, faXmark } from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia } from "react-syntax-highlighter/dist/esm/styles/prism";
import { sendEvent } from "../../api/api";
import { useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function CurlConsoleModal({ token, onClose }) {
  const url = `${API_BASE_URL}/events`;
  const method = "POST";

  const [topic, setTopic] = useState("SystemMonitor");
  const [message, setMessage] = useState("ALIVE");
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const body = JSON.stringify(
    {
      token: token,
      topic: topic,
      message: message,
    },
    null,
    2
  );

  const handleRequest = async () => {
    try {
      const res = await sendEvent(token, topic, message);
      setResponse(res);
      setError(null);
    } catch (err) {
      console.error("Error sending request:", err);
      setError("Error sending request: " + err.message);
      setResponse(null);
    }
  };

  const handleCopy = () => {
    const curlRequest = `curl -X ${method} ${url} \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;
    navigator.clipboard.writeText(curlRequest).then(() => {
      alert("Curl command copied to clipboard!");
    });
  };

  const curlRequest = `curl -X ${method} ${url} \\\n  -H "Content-Type: application/json" \\\n  -d '${body}'`;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-3/4 lg:w-3/5 py-3 px-4 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold"> </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <label className="font-semibold">Topic:</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>

          <div className="flex items-center space-x-2">
            <label className="font-semibold">Message:</label>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>

        <button
          onClick={handleRequest}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
        >
          <FontAwesomeIcon icon={faPlay} className="mr-2" />
          Send Request
        </button>

        <div className={`${response || error ? "gap-6" : ""} flex`}>
          <div
            className={`${
              response || error ? "w-1/2" : "w-max m-auto"
            } relative`}
          >
            <div className="absolute top-4 right-3">
              <button
                onClick={handleCopy}
                className="bg-gray-700 text-white px-2 py-1 rounded hover:bg-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            </div>
            <SyntaxHighlighter
              language="bash"
              style={okaidia}
              customStyle={{
                fontSize: "0.7rem",
                backgroundColor: "#1e293b",
                color: "#fff",
                overflow: "auto",
              }}
            >
              {curlRequest}
            </SyntaxHighlighter>
          </div>

          <div className={`${response || error ? "w-1/2 m-2" : "w-0"}`}>
            <div className="w-full h-full">
              {response && (
                <div className="p-4 bg-green-100 rounded h-full text-xs w-full overflow-auto">
                  <h3 className="font-semibold">Response</h3>
                  <pre className="whitespace-pre-wrap break-all">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-100 rounded text-xs w-full overflow-auto">
                  <h3 className="font-semibold">Error</h3>
                  <pre className="whitespace-pre-wrap break-all">{error}</pre>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4">
          <h2 className="text-lg font-bold mb-2">Supported Message Types</h2>
          <p className="text-gray-600 mb-4">
            Loghook supports various types of messages, and the most recent
            message sent to a topic dictates how the topic is displayed. The
            supported message types are:
          </p>
          <ul className="list-disc list-inside text-gray-600 text-sm">
            <li>
              <strong>ALIVE</strong> - Indicates that a system or service is
              alive and running. It is typically used for health checks.
            </li>
            <li>
              <strong>INFO</strong> - General information messages about the
              system status or processes.
            </li>
            <li>
              <strong>WARNING</strong> - Alerts about potential issues or
              abnormal behavior that require attention.
            </li>
            <li>
              <strong>ERROR</strong> - Critical error messages indicating system
              or process failures.
            </li>
            <li>
              <strong>Text</strong> - Simple text messages that display general
              information or status updates without any specific format.
            </li>
            <li>
              <strong>URL</strong> - Sends actionable URLs or external links.
              These are displayed in ActionCards, which allow users to interact
              with or trigger actions via the URL.
            </li>
            <li>
              <strong>Media (Image/Video)</strong> - Automatically detects and
              displays media such as images (e.g., .png, .jpg, .gif) or videos
              (e.g., .mp4, .avi) when URLs point to media files.
            </li>
          </ul>
          <p className="text-gray-600 mt-4 text-sm">
            <strong>Note:</strong> The last message sent to a topic determines
            how the topic is displayed in the dashboard. For example, if the
            last message is an image URL, the topic will be displayed as a
            MediaCard, or if it's a text message, it will be displayed as a
            simple TopicCard.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CurlConsoleModal;
